import { CaretDown, CaretUp } from '@carbon/icons-react'
import React, { useEffect, useMemo, useState } from 'react'
import {
  downloadSalesforceReport,
  getSalasForceUserPaginatedData
} from '../../api/salesforceAudit/salesforceAudit.service'

import { DDSTypography } from 'den-design-system'
import NoRecord from '../../components/common/NoContent/NoRecord'
import NoRecordData from '../../assets/images/setting.svg'
import NoRecordImage from '../../assets/images/folder-not-found 1.svg'
import { SalesforceAuditStore } from '../../store/SalesforceAuditStore'
import Search from '../../components/New/Search/Search'
import Spinner from '../../components/common/Spinner/Spinner'
import Table from '../../components/common/DDS/Table/Table'
import Tabs from '../../components/common/DDS/Tabs/Tabs'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import Tooltip from '../../components/New/Tooltip/Tooltip'
import { convertJsonToCsvDownload } from '../../utils/FileUtils'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { timeFormats } from '../../enum/TIME'
import { timeZone } from '../../utils/DateTimeUtils'
import { useSearchParams } from 'react-router-dom'
import { useStoreContext } from '../../store/StoreContext'

const SFUsersOverview: React.FC = () => {
  const store = useStoreContext()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [searchColumn, setSearchColumn] = useState('all')
  const [usersPaginatedData, setUserPaginatedData] = useState<any[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [tabIndex, setTabIndex] = React.useState<string>(
    searchParams.get('tab') ?? '1'
  )
  const [loading, setLoading] = useState<boolean>(true)
  const [currentPageActive, setCurrentPageActive] = useState(1)
  const [currentPageInactive, setCurrentPageInactive] = useState(1)
  const { salesforceAuditStore } = useStoreContext()
  const salesforceAuditId = salesforceAuditStore.auditData?.salesforceAuditId

  const totalUserCount = salesforceAuditStore.getAuditData()?.totalUserCount
  const totalActiveUserCount =
    salesforceAuditStore.getAuditData()?.activeUserCount

  const inActiveUserCount = totalUserCount - totalActiveUserCount

  const fetchSalesforcePaginatedUsers = async () => {
    store.uiStore.setGlobalLoader(true)
    setLoading(true)

    try {
      const response: any = await getSalasForceUserPaginatedData({
        $salesforceAuditId: salesforceAuditId as string,
        page: tabIndex === '1' ? currentPageActive : currentPageInactive,
        size: SalesforceAuditStore.userPageSize,
        isActive: tabIndex === '1' ? true : false
      })

      if (response) {
        setUserPaginatedData(response.data)
        salesforceAuditStore.setUserLicensePaginatedData(response.data)
      }
    } catch (err) {
      console.error('Error fetching Salesforce users:', err)
    } finally {
      setLoading(false)
      store.uiStore.setGlobalLoader(false)
    }
  }

  const getUserById = (id: string) => {
    const foundUser = userPaginatedData.find(
      (user: any) => user?.record?.Id === id
    )

    return foundUser
  }

  useEffect(() => {
    if (salesforceAuditId) {
      fetchSalesforcePaginatedUsers()
    }
  }, [salesforceAuditId, currentPageActive, tabIndex, currentPageInactive])

  useEffect(() => {
    setSearchTerm('')
    setActiveSearchTerm('')
    setSearchColumn('all')
    setCurrentPageActive(1)
    setCurrentPageInactive(1)
  }, [tabIndex])

  const userPaginatedData = [...usersPaginatedData]
  const allData = userPaginatedData

  const activeData = allData?.filter((item: any) => {
    return item.record.IsActive === true
  })

  const inactiveData = allData?.filter((item: any) => {
    return item.record.IsActive === false
  })

  const [sortConfig, setSortConfig] = useState<{
    key: string | null
    direction: 'asc' | 'desc' | null
  }>({
    key: null,
    direction: null
  })

  const toggleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key: null, direction: null }
    })
  }

  const activeDataFilteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm.trim().toLowerCase()
    if (!trimmedSearch) return activeData

    return activeData.filter((item: any) => {
      const userName = (item?.record?.Name || '').toLowerCase()
      const userLicenseType = (
        item?.record?.Profile?.UserLicense?.Name || ''
      ).toLowerCase()
      const profile = (item?.record?.Profile?.Name || '').toLowerCase()
      const role = (item?.record?.UserRole?.Name || '').toLowerCase()
      const email = (item?.record?.Email || '').toLowerCase()

      // Prepare flexible date formats for LastLoginDate
      let formattedDates: string[] = []
      if (item?.record?.LastLoginDate) {
        const m = moment(item.record.LastLoginDate)
        formattedDates = [
          m.format('MMMM D YYYY').toLowerCase(),
          m.format('D MMMM YYYY').toLowerCase(),
          m.format('MMM D').toLowerCase(),
          m.format('D MMM').toLowerCase(),
          m.format('MMMM D').toLowerCase(),
          m.format('D MMMM').toLowerCase(),
          m.format('YYYY').toLowerCase(),
          m.format('MMMM').toLowerCase(),
          m.format('D').toLowerCase()
        ]
      }

      switch (searchColumn) {
        case 'Name':
          return userName.includes(trimmedSearch)
        case 'UserLicenseType':
          return userLicenseType.includes(trimmedSearch)
        case 'Profile':
          return profile.includes(trimmedSearch)
        case 'UserRole':
          return role.includes(trimmedSearch)
        case 'Email':
          return email.includes(trimmedSearch)
        case 'LastLoginDate':
          return formattedDates.some((date) => date.includes(trimmedSearch))
        case 'all':
        default:
          return (
            userName.includes(trimmedSearch) ||
            userLicenseType.includes(trimmedSearch) ||
            profile.includes(trimmedSearch) ||
            role.includes(trimmedSearch) ||
            email.includes(trimmedSearch) ||
            formattedDates.some((date) => date.includes(trimmedSearch))
          )
      }
    })
  }, [activeData, activeSearchTerm, searchColumn])

  const activeSortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return activeDataFilteredData

    return [...activeDataFilteredData].sort((a: any, b: any) => {
      const { key, direction } = sortConfig

      const getValue = (item: any) => {
        switch (key) {
          case 'Name':
            return item?.record?.Name || ''
          case 'UserLicenseType':
            return item?.record?.Profile?.UserLicense?.Name || ''
          case 'Profile':
            return item?.record?.Profile?.Name || ''
          case 'UserRole':
            return item?.record?.UserRole?.Name || ''
          case 'Email':
            return item?.record?.Email || ''
          case 'LastLoginDate':
            return item?.record?.LastLoginDate
              ? new Date(item.record.LastLoginDate).getTime()
              : 0
          default:
            return ''
        }
      }

      const valA = getValue(a)
      const valB = getValue(b)

      if (key === 'LastLoginDate') {
        return direction === 'asc' ? valA - valB : valB - valA
      }

      if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
        return direction === 'asc'
          ? Number(valA) - Number(valB)
          : Number(valB) - Number(valA)
      }

      return direction === 'asc'
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA))
    })
  }, [activeDataFilteredData, sortConfig])

  const inActiveFilteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm.trim().toLowerCase()
    if (!trimmedSearch) return inactiveData

    return inactiveData.filter((item: any) => {
      const userName = (item?.record?.Name || '').toLowerCase()
      const userLicenseType = (
        item?.record?.Profile?.UserLicense?.Name || ''
      ).toLowerCase()
      const profile = (item?.record?.Profile?.Name || '').toLowerCase()
      const role = (item?.record?.UserRole?.Name || '').toLowerCase()
      const email = (item?.record?.Email || '').toLowerCase()

      let formattedDates: string[] = []
      if (item?.record?.LastLoginDate) {
        const m = moment(item.record.LastLoginDate)
        formattedDates = [
          m.format('MMMM D YYYY').toLowerCase(),
          m.format('D MMMM YYYY').toLowerCase(),
          m.format('MMM D').toLowerCase(),
          m.format('D MMM').toLowerCase(),
          m.format('MMMM D').toLowerCase(),
          m.format('D MMMM').toLowerCase(),
          m.format('YYYY').toLowerCase(),
          m.format('MMMM').toLowerCase(),
          m.format('D').toLowerCase()
        ]
      }

      switch (searchColumn) {
        case 'Name':
          return userName.includes(trimmedSearch)
        case 'UserLicenseType':
          return userLicenseType.includes(trimmedSearch)
        case 'Profile':
          return profile.includes(trimmedSearch)
        case 'UserRole':
          return role.includes(trimmedSearch)
        case 'Email':
          return email.includes(trimmedSearch)
        case 'LastLoginDate':
          return formattedDates.some((date) => date.includes(trimmedSearch))
        case 'all':
        default:
          return (
            userName.includes(trimmedSearch) ||
            userLicenseType.includes(trimmedSearch) ||
            profile.includes(trimmedSearch) ||
            role.includes(trimmedSearch) ||
            email.includes(trimmedSearch) ||
            formattedDates.some((date) => date.includes(trimmedSearch))
          )
      }
    })
  }, [inactiveData, activeSearchTerm, searchColumn])

  const inActiveSortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return inActiveFilteredData

    return [...inActiveFilteredData].sort((a: any, b: any) => {
      const { key, direction } = sortConfig

      const getValue = (item: any) => {
        switch (key) {
          case 'Name':
            return item?.record?.Name || ''
          case 'UserLicenseType':
            return item?.record?.Profile?.UserLicense?.Name || ''
          case 'Profile':
            return item?.record?.Profile?.Name || ''
          case 'UserRole':
            return item?.record?.UserRole?.Name || ''
          case 'Email':
            return item?.record?.Email || ''
          case 'LastLoginDate':
            return item?.record?.LastLoginDate
              ? new Date(item.record.LastLoginDate).getTime()
              : 0
          default:
            return ''
        }
      }

      const valA = getValue(a)
      const valB = getValue(b)

      if (key === 'LastLoginDate') {
        return direction === 'asc' ? valA - valB : valB - valA
      }

      if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
        return direction === 'asc'
          ? Number(valA) - Number(valB)
          : Number(valB) - Number(valA)
      }

      return direction === 'asc'
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA))
    })
  }, [inActiveFilteredData, sortConfig])

  const userActivePaginatedData = activeSortedData.map(
    (item: any) => item?.record
  )
  const userInActivePaginatedData = inActiveSortedData.map(
    (item: any) => item?.record
  )

  const paginatedActive = userActivePaginatedData

  const paginatedInactive = userInActivePaginatedData

  useEffect(() => {
    const organizationId = store.scopeStore.getScope()[
      'organization-id'
    ] as string
    const projectId = store.scopeStore.getScope()['project-id'] as string
    const environmentId = store.scopeStore.getScope()[
      'environment-id'
    ] as string

    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Salesforce Audit',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_DASHBOARD,
          organizationId,
          projectId,
          environmentId
        )
      },
      {
        label: (
          <div
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            className='cursor-not-allowed'
          >
            <div
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              className='pointer-events-none'
            >
              Users
            </div>
          </div>
        ),
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_USERS_OVERVIEW,
          organizationId,
          projectId,
          environmentId
        )
      },
      {
        label: tabIndex === '1' ? 'Active Users' : 'Inactive Users',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_USERS_OVERVIEW,
          organizationId,
          projectId,
          environmentId
        )
      }
    ])
  }, [tabIndex])

  const downloadRecords = () => {
    store.uiStore.setGlobalLoader(true)
    setLoading(true)

    const payload = {
      salesforceAuditId: salesforceAuditId as string,
      isActive: tabIndex === '1' ? true : false,
      format: 'csv'
    }

    downloadSalesforceReport(payload)
      .then((response: any) => {
        const orgTZ = store.userStore.getOrganizationPreference().timeZone
        let usersData: any[] = response.data || []

        if (!usersData.length) {
          ToastNotification({
            type: 'error',
            message: 'No users found for selected filters'
          })
          return
        }

        usersData = usersData.map((user) => ({
          email: user.email || '-',
          fullName: user.fullName || '-',
          profile: user.profile || '-',
          role: user.role || '-',
          isActive: user.isActive ?? false,
          lastLoginDate:
            user.lastLoginDate && user.lastLoginDate !== '-'
              ? timeZone(
                  orgTZ,
                  user.lastLoginDate,
                  timeFormats.fullMonthDateYearTime
                )
              : '-',
          licenseId: user.licenseId || '-',
          licenseName: user.licenseName || '-'
        }))

        if (!usersData.length) {
          ToastNotification({
            type: 'error',
            message: 'No users found for selected filters'
          })
          return
        }

        const date = new Date()
        const formattedDate = `${(date.getMonth() + 1)
          .toString()
          .padStart(
            2,
            '0'
          )}-${date.getDate().toString().padStart(2, '0')}-${date.getFullYear()}`

        tabIndex === '1'
          ? convertJsonToCsvDownload(
              usersData,
              `Salesforce_Active_User_Report_${formattedDate}.csv`
            )
          : convertJsonToCsvDownload(
              usersData,
              `Salesforce_InActive_User_Report_${formattedDate}.csv`
            )

        ToastNotification({
          type: 'success',
          message: `Successfully exported ${usersData.length} user(s)`
        })
      })
      .catch(() => {
        ToastNotification({
          type: 'error',
          message: 'Error downloading Salesforce report'
        })
      })
      .finally(() => {
        store.uiStore.setGlobalLoader(false)
        setLoading(false)
      })
  }

  const activeColumns = [
    {
      columnHeaderStyle: { width: '160px' },
      columnDataStyle: { width: '160px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              {`Username`}
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Name')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Id',
      render: (item: any) => {
        const getUser = getUserById(item)
        const user = getUser.record

        return (
          <div className='flex justify-start text-start row items-start'>
            <Tooltip
              id={`${user?.Name}-tooltip`}
              label={user?.Name}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof user?.Name === 'string' && user?.Name.length <= 22
                  ? '!hidden'
                  : ''
              }`}
            >
              <DDSTypography.Paragraph
                size='para'
                variant='medium'
                className='tooltip-container sm:block overflow-hidden truncate min-w-[160px] max-w-[160px]'
                color={pallete.colors.textDark4}
              >
                {user?.Name ? user.Name : '-'}
              </DDSTypography.Paragraph>
            </Tooltip>
          </div>
        )
      }
    },
    {
      columnHeaderStyle: { width: '160px' },
      columnDataStyle: { width: '160px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              {`License Type`}
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('UserLicenseType')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'UserLicenseType' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'UserLicenseType' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Id',
      render: (item: any) => {
        const getUser = getUserById(item)
        const user = getUser.record
        const shouldShowTooltip =
          typeof user?.Profile?.UserLicense?.Name === 'string' &&
          user?.Profile?.UserLicense?.Name.length >= 20

        return shouldShowTooltip ? (
          <Tooltip
            id={`${user?.Profile?.UserLicense?.Name}-tooltip`}
            label={user?.Profile?.UserLicense?.Name}
            className='override-tooltip-arrow max-w-[90%] break-all'
          >
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              className='truncate max-w-[160px]'
              color={pallete.colors.textDark4}
            >
              {user?.Profile?.UserLicense?.Name || '-'}
            </DDSTypography.Paragraph>
          </Tooltip>
        ) : (
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            className='truncate max-w-[200px]'
            color={pallete.colors.textDark4}
          >
            {user?.Profile?.UserLicense?.Name || '-'}
          </DDSTypography.Paragraph>
        )
      }
    },
    {
      columnHeaderStyle: { width: '160px' },
      columnDataStyle: { width: '160px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Profile
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Profile')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Profile' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Profile' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Profile',
      render: (text: any) => {
        const shouldShowTooltip =
          typeof text?.Name === 'string' && text.Name.length > 24

        return shouldShowTooltip ? (
          <Tooltip
            id={`${text.Name}-tooltip`}
            label={text.Name}
            className='override-tooltip-arrow max-w-[90%] break-all'
          >
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              className='truncate max-w-[160px]'
              color={pallete.colors.textDark4}
            >
              {text.Name || '-'}
            </DDSTypography.Paragraph>
          </Tooltip>
        ) : (
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
            className='truncate max-w-[160px]'
          >
            {text?.Name || '-'}
          </DDSTypography.Paragraph>
        )
      }
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Role
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('UserRole')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'UserRole' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'UserRole' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'UserRole',
      render: (text: any) => {
        const shouldShowTooltip =
          typeof text?.Name === 'string' && text.Name.length > 24

        return shouldShowTooltip ? (
          <Tooltip
            id={`${text.Name}-tooltip`}
            label={text.Name}
            className='override-tooltip-arrow max-w-[90%] break-all'
          >
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              className='truncate max-w-[160px]'
              color={pallete.colors.textDark4}
            >
              {text.Name || '-'}
            </DDSTypography.Paragraph>
          </Tooltip>
        ) : (
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
            className='truncate max-w-[160px]'
          >
            {text?.Name || '-'}
          </DDSTypography.Paragraph>
        )
      }
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Email
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Email')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Email' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Email' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Email',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start '>
          <div>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 25 ? text : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[160px]'
                >
                  {text || '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Last Login
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastLoginDate')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastLoginDate' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastLoginDate' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastLoginDate',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {!text
              ? '-'
              : moment(text).format(timeFormats.shortMonthDateYearTime)}
          </DDSTypography.Paragraph>
        </div>
      )
    }
  ]

  const inActiveColumns = [
    {
      columnHeaderStyle: { width: '160px' },
      columnDataStyle: { width: '160px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              {`Username`}
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Name')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Id',
      render: (item: any) => {
        const getUser = getUserById(item)
        const user = getUser.record
        return (
          <div className='flex justify-start text-start row items-start'>
            <Tooltip
              id={`${user?.Name}-tooltip`}
              label={user?.Name}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof user?.Name === 'string' && user?.Name.length <= 22
                  ? '!hidden'
                  : ''
              }`}
            >
              <DDSTypography.Paragraph
                size='para'
                variant='medium'
                className='tooltip-container sm:block overflow-hidden truncate min-w-[160px] max-w-[160px]'
                color={pallete.colors.textDark4}
              >
                {user?.Name ? user.Name : '-'}
              </DDSTypography.Paragraph>
            </Tooltip>
          </div>
        )
      }
    },
    {
      columnHeaderStyle: { width: '160px' },
      columnDataStyle: { width: '160px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              {`License Type`}
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('UserLicenseType')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'UserLicenseType' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'UserLicenseType' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Id',
      render: (item: any) => {
        const getUser = getUserById(item)
        const user = getUser.record
        const shouldShowTooltip =
          typeof user?.Profile?.UserLicense?.Name === 'string' &&
          user?.Profile?.UserLicense?.Name.length >= 20

        return shouldShowTooltip ? (
          <Tooltip
            id={`${user?.Profile?.UserLicense?.Name}-tooltip`}
            label={user?.Profile?.UserLicense?.Name}
            className='override-tooltip-arrow max-w-[90%] break-all'
          >
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              className='truncate max-w-[160px]'
              color={pallete.colors.textDark4}
            >
              {user?.Profile?.UserLicense?.Name || '-'}
            </DDSTypography.Paragraph>
          </Tooltip>
        ) : (
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            className='truncate max-w-[160px]'
            color={pallete.colors.textDark4}
          >
            {user?.Profile?.UserLicense?.Name || '-'}
          </DDSTypography.Paragraph>
        )
      }
    },
    {
      columnHeaderStyle: { width: '160px' },
      columnDataStyle: { width: '160px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Profile
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Profile')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Profile' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Profile' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Profile',
      render: (text: any) => {
        const shouldShowTooltip =
          typeof text?.Name === 'string' && text.Name.length > 24

        return shouldShowTooltip ? (
          <Tooltip
            id={`${text.Name}-tooltip`}
            label={text.Name}
            className='override-tooltip-arrow break-all max-w-[90%] '
          >
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              className='truncate max-w-[160px]'
              color={pallete.colors.textDark4}
            >
              {text.Name || '-'}
            </DDSTypography.Paragraph>
          </Tooltip>
        ) : (
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
            className='truncate max-w-[160px]'
          >
            {text?.Name || '-'}
          </DDSTypography.Paragraph>
        )
      }
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Role
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('UserRole')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'UserRole' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'UserRole' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'UserRole',
      render: (text: any) => {
        const shouldShowTooltip =
          typeof text?.Name === 'string' && text.Name.length > 24

        return shouldShowTooltip ? (
          <Tooltip
            id={`${text.Name}-tooltip`}
            label={text.Name}
            className='override-tooltip-arrow break-all max-w-[90%] '
          >
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              className='truncate max-w-[160px]'
              color={pallete.colors.textDark4}
            >
              {text?.Name || '-'}
            </DDSTypography.Paragraph>
          </Tooltip>
        ) : (
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
            className='truncate max-w-[160px]'
          >
            {text?.Name || '-'}
          </DDSTypography.Paragraph>
        )
      }
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Email
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Email')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Email' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Email' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Email',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start '>
          <div>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 25 ? text : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[160px]'
                >
                  {text || '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Last Login
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastLoginDate')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastLoginDate' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastLoginDate' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastLoginDate',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {!text
              ? '-'
              : moment(text).format(timeFormats.shortMonthDateYearTime)}
          </DDSTypography.Paragraph>
        </div>
      )
    }
  ]

  const tabs = [
    {
      id: '1',
      label:
        tabIndex === '1' ? (
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.primary2}
          >
            Active Users ({totalActiveUserCount})
          </DDSTypography.Title>
        ) : (
          <DDSTypography.Title
            type='h5'
            variant='medium'
            color={pallete.colors.textDark5}
          >
            Active Users ({totalActiveUserCount})
          </DDSTypography.Title>
        ),
      children: (
        <div className='flex flex-col py-[24px]'>
          <div className='relative pb-[12px]'>
            <Search
              id='search'
              name='Search'
              placeholder='Search'
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value
                setSearchTerm(value)
                setShowDropdown(value.length > 0)
                if (value.trim() === '') {
                  setActiveSearchTerm('')
                }
              }}
              autoComplete='off'
              width='100%'
              onPressEnter={() => {
                setActiveSearchTerm(searchTerm.trim())
                setCurrentPageActive(1)
                setShowDropdown(false)
              }}
              onBlur={() => {
                setTimeout(() => {
                  setShowDropdown(false)
                  setCurrentPageActive(1)
                }, 200)
              }}
            />

            {showDropdown && (
              <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
                {[
                  { label: 'User Name', value: 'Name' },
                  { label: 'License Type', value: 'UserLicenseType' },
                  { label: 'Profile', value: 'Profile' },
                  { label: 'Role', value: 'UserRole' },
                  { label: 'Last Login', value: 'LastLoginDate' },
                  { label: 'Email', value: 'Email' },
                  { label: 'All Columns', value: 'all' }
                ].map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      setSearchColumn(option.value)
                      setCurrentPageActive(1)
                      setActiveSearchTerm(searchTerm.trim())
                      setShowDropdown(false)
                    }}
                    className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
                  >
                    <span className='font-mulish text-black'>{searchTerm}</span>
                    <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                      in: {option.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {loading || store.uiStore.getGlobalLoader() ? (
            <div className='flex justify-center items-center h-[45vh]'>
              <Spinner />
            </div>
          ) : activeSortedData?.length > 0 ? (
            <Table
              columns={activeColumns}
              data={paginatedActive}
              totalCount={totalActiveUserCount}
              currentPage={currentPageActive}
              rowsPerPage={SalesforceAuditStore.userPageSize}
              onPageChange={(page) => setCurrentPageActive(page)}
            />
          ) : searchTerm?.trim() ? (
            <NoRecord
              imageSrc={NoRecordImage}
              style={{ height: '45vh' }}
              text={<div>No Users match your search criteria.</div>}
            />
          ) : (
            <NoRecord
              imageSrc={NoRecordData}
              style={{ height: '45vh' }}
              text={<div>No Users found.</div>}
            />
          )}
        </div>
      )
    },
    {
      id: '2',
      label:
        tabIndex === '2' ? (
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.primary2}
          >
            Inactive Users ({inActiveUserCount})
          </DDSTypography.Title>
        ) : (
          <DDSTypography.Title
            type='h5'
            variant='medium'
            color={pallete.colors.textDark5}
          >
            Inactive Users ({inActiveUserCount})
          </DDSTypography.Title>
        ),
      children: (
        <div className='flex flex-col py-[24px]'>
          <div className='relative pb-[12px]'>
            <Search
              id='search'
              name='Search'
              placeholder='Search'
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value
                setSearchTerm(value)
                setShowDropdown(value.length > 0)
                if (value.trim() === '') {
                  setActiveSearchTerm('')
                }
              }}
              autoComplete='off'
              width='100%'
              onPressEnter={() => {
                setActiveSearchTerm(searchTerm.trim())
                setCurrentPageActive(1)
                setShowDropdown(false)
              }}
              onBlur={() => {
                setTimeout(() => {
                  setShowDropdown(false)
                  setCurrentPageActive(1)
                }, 200)
              }}
            />

            {showDropdown && (
              <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
                {[
                  { label: 'User Name', value: 'Name' },
                  { label: 'License Type', value: 'UserLicenseType' },
                  { label: 'Profile', value: 'Profile' },
                  { label: 'Role', value: 'UserRole' },
                  { label: 'Last Login', value: 'LastLoginDate' },
                  { label: 'Email', value: 'Email' },
                  { label: 'All Columns', value: 'all' }
                ].map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      setSearchColumn(option.value)
                      setCurrentPageActive(1)
                      setActiveSearchTerm(searchTerm.trim())
                      setShowDropdown(false)
                    }}
                    className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
                  >
                    <span className='font-mulish text-black'>{searchTerm}</span>
                    <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                      in: {option.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {loading ? (
            <div className='flex justify-center items-center h-[45vh]'>
              <Spinner />
            </div>
          ) : inActiveSortedData?.length > 0 ? (
            <Table
              columns={inActiveColumns}
              data={paginatedInactive}
              totalCount={inActiveUserCount}
              currentPage={currentPageInactive}
              rowsPerPage={SalesforceAuditStore.userPageSize}
              onPageChange={(page) => setCurrentPageInactive(page)}
            />
          ) : searchTerm?.trim() ? (
            <NoRecord
              imageSrc={NoRecordImage}
              style={{ height: '45vh' }}
              text={<div>No Users match your search criteria.</div>}
            />
          ) : (
            <NoRecord
              imageSrc={NoRecordData}
              style={{ height: '45vh' }}
              text={<div>No Users found.</div>}
            />
          )}
        </div>
      )
    }
  ]

  return (
    <div className='flex flex-col w-full py-[24px] '>
      <Tabs
        tabOptionsStyle={{
          minWidth: 'fit-content',
          width: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '8px 12px',
          marginRight: '8px'
        }}
        tabOptions={tabs}
        id='tabs-grouped-api'
        onChange={(index: any) => {
          setTabIndex(index.toString())
          setSearchParams({
            ...searchParams,
            tab: index.toString()
          })
        }}
        exportOption={true}
        showActionButton={true}
        onActionButtonPress={downloadRecords}
        actionButtonLabel='Export'
        actionButtonDisabled={
          store.uiStore.getGlobalLoader() ||
          (tabIndex === '1' && activeSortedData.length === 0) ||
          (tabIndex === '2' && inActiveSortedData?.length === 0)
        }
        activeTab={tabIndex}
      />
    </div>
  )
}

export default observer(SFUsersOverview)
